import { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageLightbox } from "@/components/image-lightbox";
import { assetVisual } from "@/lib/asset-visual";
import { cn } from "@/lib/utils";
import {
    AlertCircle,
    AlertTriangle,
    ClipboardCheck,
    Gauge,
    ListChecks,
    ExternalLink,
    FileSignature,
    Pencil,
    Plus,
    Trash2,
    UserCheck,
    Wrench,
} from "lucide-react";
import { displayDeviceLocation } from "@/lib/device-location";
import { EmptyRow } from "./empty-row";
import { FailProtocolFold, type OfficeProtocol } from "./fail-protocol-card";
import type { JobAssetRow, JobRepairResult, JobTestResult } from "./types";
import { testStateLabel, testStateTone } from "@/lib/test-state";
import { LegProofChip, type JobProof } from "@/components/proof/job-proof";
import { HowItWorks } from "@/components/how-it-works";

interface PhotoRef {
    slot: string;
    url: string;
}

function photosOf(test: JobTestResult | undefined): PhotoRef[] {
    if (!test?.photos) return [];
    return Object.entries(test.photos).flatMap(([slot, urls]) => (urls ?? []).map((url) => ({ slot, url })));
}

/**
 * Mirror of the server's `isPaperConvertible` (services/paperTestConversion.ts).
 * The rip cord (#1300) exists for devices the app left UNRESOLVED — a device
 * that already carries a real (non-partial) result isn't stuck, and paper must
 * never silently overwrite a reading the tech actually took.
 *
 * Exported so the rule is unit-testable and stays honest against the server's.
 */
export function canUsePaper(asset: JobAssetRow, test: JobTestResult | undefined): boolean {
    const overall = (test?.overall_result ?? "").toLowerCase();
    if (test && overall !== "incomplete") return false;
    if (test?.paper_readings_waiting) return false; // already with the tech
    if (asset.disposition) return false;
    if ((asset.required_action ?? "") === "replace") return false;
    if (asset.parent_asset_id) return false; // bypass legs test with their main
    const status = asset.asset_status ?? "active";
    if (status === "retired" || status === "replaced") return false;
    return true;
}

/**
 * #1300 — a result the office read off a signed paper form because the app
 * workflow never finished. Says so plainly on the row: the paper is the record,
 * and who typed it is not who tested it.
 */
/** Sunny Woods #092678193: a test saved on this job for a device that lives at
 *  ANOTHER site. Say where it lives, with a link, so the office can see why it
 *  is here. It never counts toward this job's reports or filings. */
function OtherSiteLine({ test }: { test: JobTestResult | undefined }) {
    if (!test?.other_site_id) return null;
    return (
        <div className="mt-1 flex items-start gap-1 text-xs text-amber-700" data-testid="other-site-line">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
                Test for a device at another site —{" "}
                <Link to={`/sites/${test.other_site_id}`} className="font-medium underline">
                    {test.other_site_name || "open that site"}
                </Link>
                . It doesn't hold this job's paperwork.
            </span>
        </div>
    );
}

function PaperConversionLine({ test }: { test: JobTestResult | undefined }) {
    if (test?.entry_source !== "paper_conversion") return null;
    return (
        <>
            {test.no_readings_cant_file && (
                <div
                    className="mt-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-semibold text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                    data-testid="no-readings-cant-file"
                >
                    Failed with no readings: the city report can&apos;t be filed from this.
                </div>
            )}
            <PaperConversionSource test={test} />
        </>
    );
}

function PaperConversionSource({ test }: { test: JobTestResult }) {
    return (
        <div className="mt-1 flex items-start gap-1 text-xs text-amber-700">
            <FileSignature className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
                From the signed paper report
                {test.converted_by_name ? ` — entered by ${test.converted_by_name}` : ""}
                {test.conversion_reason ? `. ${test.conversion_reason}` : ""}
            </span>
        </div>
    );
}

/**
 * Attribution line (Issue #269): show who to ask about a device's result.
 * The performing internal tech comes first; an external/portal tester falls
 * back to `tester_name`; and when a helper typed the row (Helper Mode) and
 * that differs from the performer, we name them too. Renders nothing when the
 * result carries no author at all (older rows).
 */
function TesterLine({ test }: { test: JobTestResult | undefined }) {
    if (!test) return null;
    const primary = test.performed_by_name || test.tester_name;
    const helper =
        test.entered_by_name && test.entered_by_name !== test.performed_by_name ? test.entered_by_name : null;
    if (!primary && !helper) return null;
    return (
        <div className="mt-1 flex items-center gap-1 text-xs text-foreground/70">
            <UserCheck className="h-3 w-3 shrink-0 text-foreground/50" aria-hidden="true" />
            {primary && (
                <span>
                    Tested by <span className="font-medium text-foreground/90">{primary}</span>
                </span>
            )}
            {helper && <span className="text-foreground/60">· entered by {helper}</span>}
        </div>
    );
}

/**
 * Repair readback: who did it, who typed it, the notes, and — amber — why
 * there are no photos when it was saved without them (the tech's reason, or
 * "Recorded from the office"). Mockup region E, mockups/repair-without-photos.html.
 */
export function RepairLine({
    repair,
    onRemove,
}: {
    repair: JobRepairResult | undefined;
    /** Office-only "Remove repair" (mockup region 2b). */
    onRemove?: () => void;
}) {
    if (!repair) return null;
    const by = repair.performed_by_name;
    const typedBy = repair.entered_by_name && repair.entered_by_name !== by ? repair.entered_by_name : null;
    const notes = (repair.repair_notes ?? "").trim();
    return (
        <div className="mt-1 text-xs" data-testid="repair-line">
            {(by || typedBy) && (
                <div className="flex items-center gap-1 text-foreground/70">
                    <Wrench className="h-3 w-3 shrink-0 text-foreground/50" aria-hidden="true" />
                    {by && (
                        <span>
                            Repaired by <span className="font-medium text-foreground/90">{by}</span>
                        </span>
                    )}
                    {typedBy && <span className="text-foreground/60">· entered by {typedBy}</span>}
                    {onRemove && (
                        <>
                            <span className="text-foreground/40">·</span>
                            <button
                                type="button"
                                className="text-destructive underline-offset-2 hover:underline"
                                onClick={onRemove}
                                title="Saved on the wrong device? Remove this repair record and its provisional points"
                            >
                                Remove repair
                            </button>
                        </>
                    )}
                </div>
            )}
            {notes && <div className="mt-1 rounded bg-muted/50 p-2 whitespace-pre-wrap">{notes}</div>}
            {repair.photos_skipped_reason && (
                <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-foreground/80">
                    <span className="font-semibold text-amber-700">No repair photos.</span>{" "}
                    {repair.photos_skipped_reason}
                </div>
            )}
        </div>
    );
}

const isFail = (test: JobTestResult | undefined): boolean => (test?.overall_result ?? "").toLowerCase() === "fail";

/** True when the remarks only repeat a reason already shown under Why it failed. */
function remarksRepeatReason(test: JobTestResult): boolean {
    const remarks = (test.remarks ?? "").trim();
    return isFail(test) && remarks.length > 0 && (test.failure_reasons ?? []).some((r) => (r ?? "").trim() === remarks);
}

/** Legacy `failure_reasons` may be structured tags ("cv1_leaked") or already
 *  human sentences (from computePassFail). Underscores → spaces makes a tag
 *  readable and no-ops on a sentence (which has none); we also upper-case the
 *  first letter. */
function humanizeReason(reason: string): string {
    const spaced = reason.replace(/_/g, " ").trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Failure readback: on a FAIL row, tell the office WHY it failed in one framed
 * panel. Until now a failed device showed only a red "FAIL" chip — the repair
 * flag and the tech's reasons/notes never reached the office job page, so
 * nobody could see what was wrong without opening the report PDF.
 *
 * The narrative can live in two places: the modern unified "Notes on failure"
 * field lands in `completion_notes` (PRE-CUTOVER-UX-FEEDBACK #15), while
 * historical rows kept it in the legacy `failure_reasons` + `failure_notes`
 * columns. This panel surfaces whichever is present, so both new and old
 * failures read the same. (The caller suppresses the standalone
 * completion_notes box on fails so it isn't shown twice.)
 *
 * Renders nothing on passes, on rows with no failure content, and on bypass
 * legs (whose result — and reasons — live on the main assembly's row).
 */
function FailureReadback({ test }: { test: JobTestResult | undefined }) {
    if (!test || !isFail(test)) return null;
    const reasons = (test.failure_reasons ?? []).filter((r): r is string => !!r && r.trim().length > 0);
    // The free-text story: modern completion_notes and/or legacy failure_notes.
    // De-dupe in case a migrated row carries the same text in both.
    const narrative = [test.completion_notes, test.failure_notes]
        .map((s) => (s ?? "").trim())
        .filter((s, i, arr) => s.length > 0 && arr.indexOf(s) === i);
    if (reasons.length === 0 && narrative.length === 0 && !test.flagged_for_repair) return null;
    return (
        <div className="mt-1 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs">
            <div className="flex items-center gap-1.5 font-medium text-destructive">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Why it failed
                {test.flagged_for_repair && (
                    <Badge variant="destructive" className="ml-auto text-[10px] shrink-0">
                        Flagged for repair
                    </Badge>
                )}
            </div>
            {reasons.length > 0 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-foreground/80">
                    {reasons.map((r, i) => (
                        <li key={i}>{humanizeReason(r)}</li>
                    ))}
                </ul>
            )}
            {narrative.map((n, i) => (
                <div key={i} className="mt-1 whitespace-pre-wrap text-foreground/80">
                    {n}
                </div>
            ))}
        </div>
    );
}

function resultChip(
    test: JobTestResult | undefined,
    asset: JobAssetRow,
    hasRepair: boolean,
    inheritedTest?: JobTestResult,
) {
    if (asset.required_action === "replace") {
        // Once the swap is finalized the device is done — say "Replaced", not
        // a standing "Replace" instruction. `resolved` is the server's
        // gate-matching flag (replacement finalized + new device tested).
        return asset.resolved ? (
            <Badge variant="default" className="text-[10px] shrink-0">
                Replaced
            </Badge>
        ) : (
            <Badge variant="outline" className="text-[10px] shrink-0 text-foreground/70">
                Replace
            </Badge>
        );
    }
    if (asset.required_action === "repair" && !test) {
        return (
            <Badge variant={hasRepair ? "default" : "outline"} className="text-[10px] shrink-0">
                {hasRepair ? "Repaired" : "Repair pending"}
            </Badge>
        );
    }
    if (!test) {
        // A bypass leg carries no test row of its own — its readings live on
        // the main's row. When the main is tested the pair is tested, so show
        // the shared result instead of a misleading "Pending".
        if (inheritedTest) {
            // The bypass leg's OWN result (trust audit Phase 1), not the
            // whole row's: a main that failed doesn't make the bypass a fail.
            const own = inheritedTest.bypass_leg_result ?? inheritedTest.overall_result;
            return (
                <span className="inline-flex items-center gap-1.5">
                    <Badge
                        variant={badgeVariant(own)}
                        className="text-[10px] shrink-0"
                        title="Bypass leg — tested with its main assembly."
                        data-testid="bypass-leg-result"
                    >
                        {testStateLabel(own).toUpperCase() || "TESTED"}
                    </Badge>
                    <HowItWorks
                        title="How main and bypass results work"
                        testId="how-main-bypass"
                        steps={[
                            {
                                title: "The main and the bypass each have their own result",
                                text: "They're tested on the same visit and saved together, but a bypass that failed doesn't make the main a fail, and the other way round.",
                                picture: "main-and-bypass.png",
                                alt: "The main reading PASS and the bypass reading FAIL, both ringed",
                            },
                            {
                                title: "The quote follows the result",
                                text: "Only the one that failed gets a repair on the quote.",
                            },
                        ]}
                    />
                </span>
            );
        }
        return (
            <Badge variant="outline" className="text-[10px] shrink-0 text-foreground/70">
                Pending
            </Badge>
        );
    }
    if (test.paper_readings_waiting) {
        // The paper says FAIL; the tech hasn't typed the readings yet.
        const who = (test.paper_readings_tech_name ?? "the tech").split(" ")[0];
        return (
            <Badge
                variant="destructive"
                className="text-[10px] shrink-0"
                data-testid="paper-waiting-on-tech"
                title="The signed paper says fail. The tech enters the readings from it on his iPad."
            >
                FAIL · WAITING ON {who.toUpperCase()}
            </Badge>
        );
    }
    const redTag = (test.recommended_action ?? "").toLowerCase().includes("red");
    // A detector's main shows its own leg; a bypass-only fail no longer
    // paints the main red (Renaissance FE0694).
    const shown = test.bypass_leg_result != null && test.main_leg_result ? test.main_leg_result : test.overall_result;
    return (
        <Badge variant={badgeVariant(shown)} className={cn("text-[10px] shrink-0", redTag && "bg-red-700")}>
            {redTag ? "RED TAG" : testStateLabel(shown).toUpperCase() || "RECORDED"}
        </Badge>
    );
}

/** One look per result (lib/test-state.ts): green pass, red fail, and a grey
 *  outline for couldn't test, not finished and result not known. */
function badgeVariant(overallResult: string | null | undefined): "default" | "destructive" | "outline" {
    const tone = testStateTone(overallResult);
    return tone === "pass" ? "default" : tone === "fail" ? "destructive" : "outline";
}

/**
 * #1142 — plain English for `job_assets.disposition`. The raw values are
 * snake_case enum members and must never reach a human. Unknown values fall
 * back to a neutral heading rather than printing the enum.
 */
function dispositionLabel(disposition?: string | null): string {
    switch ((disposition ?? "").toLowerCase()) {
        case "not_located":
            return "Couldn't find it";
        case "could_not_test":
            return "Couldn't test it";
        case "skipped":
            return "Skipped";
        case "not_due":
            return "Not due";
        default:
            return "Tech's note";
    }
}

function DeviceRow({
    asset,
    test,
    hasRepair,
    repair,
    onRecordRepair,
    recordRepairForName,
    onRemoveRepair,
    onEdit,
    onUnlink,
    unlinkPending,
    onPhotoClick,
    isChild,
    bypassChildSerial,
    inheritedTest,
    protocols,
    onScope,
    scopePendingAssetId,
    onUsePaper,
    jobProof,
    onUploadReport,
}: {
    asset: JobAssetRow;
    test: JobTestResult | undefined;
    hasRepair: boolean;
    /** This device's repair row on this job, when there is one. */
    repair?: JobRepairResult;
    /** Office-only "Record repair for <tech>" (mockup region C). Absent for
     *  every other viewer. */
    onRecordRepair?: (asset: JobAssetRow) => void;
    /** First name on the button ("Record repair for Dave"). */
    recordRepairForName?: string | null;
    /** Office-only "Remove repair" on the readback. */
    onRemoveRepair?: (asset: JobAssetRow) => void;
    onEdit: (asset: JobAssetRow) => void;
    onUnlink: (asset: JobAssetRow) => void;
    unlinkPending: boolean;
    onPhotoClick: (photos: PhotoRef[], index: number) => void;
    /** Render indented under the bypass parent (grouped pair). */
    isChild?: boolean;
    /** Serial of this device's bypass leg when the pair is on the job. */
    bypassChildSerial?: string | null;
    /** For a bypass leg with no test row of its own: the main's test, whose
     *  result the pair shares. Drives the chip + date, never photos/notes. */
    inheritedTest?: JobTestResult;
    /** This device's jurisdiction fail protocol(s), folded into the row
     *  (Issue #768 — the standalone card is retired). */
    protocols?: OfficeProtocol[];
    /** #812 — office scoping control (Test / Repair / Replace). Absent for
     *  non-office viewers; the control also hides once field work exists. */
    onScope?: (asset: JobAssetRow, action: "test" | "repair" | "replace") => void;
    scopePendingAssetId?: string | null;
    /** #1300 — the rip cord. Office-only; absent for every other viewer, so a
     *  field role never sees a way to convert its own paperwork. */
    onUsePaper?: (asset: JobAssetRow) => void;
    /** Proof chips S3 (null/absent = switch off). */
    jobProof?: JobProof | null;
    onUploadReport?: (() => void) | null;
}) {
    const visual = assetVisual(asset.asset_type);
    const Icon = visual.Icon;
    const specs = [asset.size_label, asset.manufacturer, asset.model].filter(Boolean).join(" · ");
    // Import junk ("Information Contact Information") is not a location.
    const location = displayDeviceLocation(asset.location_description);
    const photos = photosOf(test);
    const photoBy = test?.performed_by_name || test?.tester_name || null;
    return (
        <div className={cn("py-3 first:pt-0 last:pb-0", isChild && "pl-6 border-l-2 border-l-primary/20 ml-4")}>
            <div className="flex items-start gap-3">
                <span
                    className={cn(
                        "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md shrink-0",
                        visual.badgeClassName,
                    )}
                    title={visual.label}
                >
                    <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{asset.label}</span>
                        {resultChip(test, asset, hasRepair, inheritedTest)}
                        {/* Proof chips S3: how sure this leg's result is (switch on only). */}
                        <LegProofChip
                            test={test}
                            inheritedTest={inheritedTest}
                            asset={asset}
                            jobProof={jobProof ?? null}
                            onUploadReport={onUploadReport ?? null}
                        />
                        {/* A tested repair device still owes its repair — the
                            test chip alone hid that (job #092678199 read FAIL
                            with no hint the repair was missing). */}
                        {asset.required_action === "repair" && test && (
                            <Badge variant={hasRepair ? "default" : "outline"} className="text-[10px] shrink-0">
                                {hasRepair ? "Repaired" : "Repair pending"}
                            </Badge>
                        )}
                        {asset.parent_asset_id && (
                            <Badge
                                variant="outline"
                                className="text-[10px] shrink-0 border-primary/30 bg-primary/10 text-primary"
                                title="Bypass leg of a DCDA/RPDA assembly — tested with its parent."
                            >
                                Bypass of {asset.parent_serial_number ?? "main"}
                            </Badge>
                        )}
                        {!asset.parent_asset_id && bypassChildSerial !== undefined && bypassChildSerial !== null && (
                            <Badge
                                variant="outline"
                                className="text-[10px] shrink-0 border-primary/30 bg-primary/10 text-primary"
                                title="This assembly's bypass leg is on the job too."
                            >
                                Bypass: {bypassChildSerial}
                            </Badge>
                        )}
                        {asset.needs_verification_reason && (
                            <Badge
                                variant="outline"
                                className="text-[10px] shrink-0 border-amber-500/40 text-amber-600 bg-amber-500/10 inline-flex items-center gap-1"
                                title={asset.needs_verification_reason}
                            >
                                <AlertCircle className="h-3 w-3" /> Verify on site
                            </Badge>
                        )}
                        {(test?.performed_at ?? inheritedTest?.performed_at) && (
                            <span className="text-[11px] text-foreground/60">
                                {new Date((test?.performed_at ?? inheritedTest?.performed_at)!).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-foreground/70 flex items-center gap-2 flex-wrap">
                        {specs && <span>{specs}</span>}
                        {asset.serial_number && <span className="font-mono">S/N {asset.serial_number}</span>}
                        {location && <span>· {location}</span>}
                    </div>
                    {/* #812 — the office's scoping control. Only before any field
                        work exists: once a test/repair/replacement is recorded the
                        scope is history, not a plan, and stops being editable. */}
                    {onScope && !test && !inheritedTest && !hasRepair && !asset.resolved && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs" data-testid="device-scope-control">
                            <label className="text-foreground/60" htmlFor={`scope-${asset.asset_id}`}>
                                This visit:
                            </label>
                            <select
                                id={`scope-${asset.asset_id}`}
                                className="h-6 rounded border border-border bg-background px-1 text-xs"
                                value={(asset.required_action || "test").toLowerCase()}
                                disabled={scopePendingAssetId === asset.asset_id}
                                onChange={(e) => onScope(asset, e.target.value as "test" | "repair" | "replace")}
                                title="What this visit does to this device. The tech's app opens the matching flow — Replace opens the guided swap."
                            >
                                <option value="test">Test</option>
                                <option value="repair">Repair</option>
                                <option value="replace">Replace</option>
                            </select>
                            {(asset.required_action || "test").toLowerCase() === "replace" && (
                                <span className="text-foreground/60">— the tech gets the guided replacement flow</span>
                            )}
                        </div>
                    )}
                    {/* #1142 — when the tech could not test a device, they are
                        REQUIRED to say why (migration 210). Office Review and
                        the backflow workflow already showed that reason; the
                        job page was the one screen that never asked the server
                        for it, so the office saw "not located" with no
                        explanation and had to go and ask. */}
                    {asset.disposition_reason && (
                        <div className="mt-1 text-xs">
                            <span className="font-medium">{dispositionLabel(asset.disposition)}:</span>{" "}
                            <span className="text-foreground/80">{asset.disposition_reason}</span>
                        </div>
                    )}
                    <TesterLine test={test ?? inheritedTest} />
                    <RepairLine repair={repair} onRemove={onRemoveRepair ? () => onRemoveRepair(asset) : undefined} />
                    <PaperConversionLine test={test ?? inheritedTest} />
                    <OtherSiteLine test={test} />
                    <FailureReadback test={test} />
                    {(protocols ?? []).map((p) => (
                        <FailProtocolFold key={p.id} protocol={p} />
                    ))}
                    {test?.recommended_action && (
                        <div className="mt-1 text-xs">
                            <span className="font-medium">Recommendation:</span>{" "}
                            {test.recommended_action.replace(/_/g, " ")}
                        </div>
                    )}
                    {/* On a FAIL row the completion note IS the failure story,
                        shown inside FailureReadback above — don't repeat it. */}
                    {test?.completion_notes && !isFail(test) && (
                        <div className="mt-1 rounded bg-muted/50 p-2 text-xs whitespace-pre-wrap">
                            {test.completion_notes}
                        </div>
                    )}
                    {/* A FAIL's reason already shows under Why it failed; an
                        "Unable to locate assembly" FAIL also stores it as the
                        remarks (10/29), so don't print it twice. */}
                    {test?.remarks && !remarksRepeatReason(test) && (
                        <div className="mt-1 text-xs italic text-foreground/70">{test.remarks}</div>
                    )}
                    {photos.length > 0 && (
                        <>
                            <div className="mt-2 flex gap-1.5 overflow-x-auto">
                                {photos.map((p, i) => (
                                    <button
                                        key={`${p.slot}-${i}`}
                                        type="button"
                                        className="h-16 w-16 shrink-0 overflow-hidden rounded border bg-muted"
                                        onClick={() => onPhotoClick(photos, i)}
                                        title={p.slot.replace(/_/g, " ")}
                                    >
                                        <img src={p.url} alt={p.slot} className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                            {/* Explicit count + author so the strip reads as evidence,
                                not decoration (Issue #768, mockup region J5). */}
                            <p className="mt-1 text-[11px] text-foreground/60">
                                {photos.length} photo{photos.length === 1 ? "" : "s"}
                                {photoBy ? ` from ${photoBy}` : ""} — tap any to view
                            </p>
                        </>
                    )}
                    {(test?.overall_result ?? "").toLowerCase() === "pass" && photos.length === 0 && (
                        <p className="mt-1 text-[11px] text-muted-foreground">No photos needed on a pass.</p>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {onRecordRepair && asset.required_action === "repair" && !hasRepair && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 px-2 text-[11px] text-amber-700 border-amber-500/40 hover:bg-amber-50"
                            onClick={() => onRecordRepair(asset)}
                            title="The repair was done on site but never saved in the app — record it for the tech"
                        >
                            <Wrench className="h-3.5 w-3.5" />
                            {recordRepairForName ? `Record repair for ${recordRepairForName}` : "Record repair"}
                        </Button>
                    )}
                    {onUsePaper && canUsePaper(asset, test) && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 px-2 text-[11px] text-amber-700 border-amber-500/40 hover:bg-amber-50"
                            onClick={() => onUsePaper(asset)}
                            title="The tech tested this and signed the paper, but the app never finished — record the paper as the result"
                        >
                            <FileSignature className="h-3.5 w-3.5" /> Use the paper
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-foreground/70 hover:text-primary"
                        onClick={() => onEdit(asset)}
                        title="Edit device"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-foreground/70 hover:text-destructive"
                        onClick={() => onUnlink(asset)}
                        disabled={unlinkPending}
                        title="Remove device from job"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * Results card (spec §3.3): Test Results + Devices table + Photos merged into
 * one row per device — identity, PASS/FAIL chip, location note, 64px photo
 * strip with lightbox, row actions. Add device lives in the card header.
 * Pre-visit it reads as the plan ("N devices expected").
 */
export const ResultsCard = forwardRef<
    HTMLDivElement,
    {
        assets: JobAssetRow[];
        testResults: JobTestResult[];
        repairResults: JobRepairResult[];
        preVisit: boolean;
        tradeHasFamily: boolean;
        addDisabled: boolean;
        addTitle: string;
        onAddDevice: () => void;
        /** Renders a "Reconcile from list" button in the header when provided (backflow jobs). */
        onReconcile?: (() => void) | null;
        onEditAsset: (asset: JobAssetRow) => void;
        onUnlinkAsset: (asset: JobAssetRow) => void;
        unlinkPending: boolean;
        /** Prompt 22: job was converted from an approved estimate but no quoted
         *  devices could be parsed — nudge the office to add them. */
        quotedDevicesNudge?: boolean;
        /** Jurisdiction fail protocols for this job, folded into their device
         *  rows (Issue #768). The query stays lifted in detail.tsx. */
        failProtocols?: OfficeProtocol[];
        /** #812 — office Test/Repair/Replace scoping. Optional so read-only
         *  renders (and tests) need no wiring; the mutation lives in
         *  detail.tsx alongside the other job mutations. */
        onScopeAsset?: (asset: JobAssetRow, action: "test" | "repair" | "replace") => void;
        scopePendingAssetId?: string | null;
        /** #1300 — the rip cord: convert a signed paper test into the device's
         *  result. Office-or-above only, so detail.tsx passes it only when the
         *  viewer holds office_review.manage; every other viewer gets no button. */
        onUsePaper?: (asset: JobAssetRow) => void;
        /** Office-only "Record repair for <tech>" — detail.tsx passes it only
         *  when the viewer holds office_review.manage. */
        onRecordRepair?: (asset: JobAssetRow) => void;
        /** The job tech's first name for that button. */
        recordRepairForName?: string | null;
        /** Office-only "Remove repair" — a repair saved on the wrong device. */
        onRemoveRepair?: (asset: JobAssetRow) => void;
        /** #1300 — link into the tech's own workflow (`/field/<trade>/:jobId`)
         *  so "just drive it for him" is discoverable. Absent = not offered. */
        fieldWorkflowHref?: string | null;
        /** Proof chips S3: how sure each leg is (null = the How-sure switch is off). */
        jobProof?: JobProof | null;
        /** The page's own Upload, for a chip's "Upload the report". */
        onUploadReport?: (() => void) | null;
    }
>(function ResultsCard(
    {
        assets,
        testResults,
        repairResults,
        preVisit,
        tradeHasFamily,
        addDisabled,
        addTitle,
        onAddDevice,
        onReconcile,
        onEditAsset,
        onUnlinkAsset,
        unlinkPending,
        quotedDevicesNudge,
        jobProof,
        onUploadReport,
        failProtocols,
        onScopeAsset,
        scopePendingAssetId,
        onUsePaper,
        onRecordRepair,
        recordRepairForName,
        onRemoveRepair,
        fieldWorkflowHref,
    },
    ref,
) {
    const [lightbox, setLightbox] = useState<{ photos: PhotoRef[]; index: number } | null>(null);

    // Devices the work order didn't name — linked from the location instead
    // (`job_assets.linked_via`). Derived rather than passed so any surface
    // rendering this card gets the callout for free.
    // "Confirm the list before dispatch" is advice for before the visit — once
    // the job is out or anything is tested, it's stale (audit 2026-10-01).
    const siteFallbackCount =
        preVisit && testResults.length === 0 ? assets.filter((a) => a.linked_via === "wo_site_fallback").length : 0;

    const testByAsset = new Map<string, JobTestResult>();
    for (const t of testResults) {
        const existing = testByAsset.get(t.asset_id);
        if (!existing || new Date(t.performed_at).getTime() > new Date(existing.performed_at).getTime()) {
            testByAsset.set(t.asset_id, t);
        }
    }
    const repairedAssets = new Set(repairResults.map((r) => r.asset_id));
    const repairByAsset = new Map<string, JobRepairResult>();
    for (const r of repairResults) repairByAsset.set(r.asset_id, r); // ordered by created_at — last wins
    // A bypass leg (parent_asset_id) is tested with its main — its readings
    // live on the parent's test row, so it never has one of its own. Count it
    // tested whenever its parent is, matching the field completion gate; else
    // a fully-tested DCDA/RPDA pair reads "1/2 tested" here forever.
    const isTested = (a: JobAssetRow) =>
        testByAsset.has(a.asset_id) || (a.parent_asset_id != null && testByAsset.has(a.parent_asset_id));
    // A device is "done" when it owes no more field work. Prefer the server's
    // gate-matching `resolved` flag; fall back to local test detection when a
    // cached/older response predates it (keeps test-only jobs correct).
    const isDone = (a: JobAssetRow) => a.resolved ?? isTested(a);
    const doneCount = assets.filter(isDone).length;
    // The progress noun follows the work: a single-action job reads its verb
    // ("tested" / "repaired" / "replaced"); a mixed list reads the neutral
    // "done". This is what stops a fully-replaced job reading "Not started".
    const actionSet = new Set(assets.map((a) => (a.required_action || "test").toLowerCase()));
    const doneNoun =
        actionSet.size === 1
            ? ({ replace: "replaced", repair: "repaired", test: "tested" }[[...actionSet][0]] ?? "done")
            : "done";
    // Tests recorded against assets no longer linked to the job — keep the
    // evidence visible instead of silently dropping it.
    const orphanTests = testResults.filter((t) => !assets.some((a) => a.asset_id === t.asset_id));

    // Fold each protocol into its device's row; protocols whose device is no
    // longer linked render at the bottom so the sprint never disappears.
    const protocolsByAsset = new Map<string, OfficeProtocol[]>();
    for (const p of failProtocols ?? []) {
        const arr = protocolsByAsset.get(p.asset_id) ?? [];
        arr.push(p);
        protocolsByAsset.set(p.asset_id, arr);
    }
    const orphanProtocols = (failProtocols ?? []).filter((p) => !assets.some((a) => a.asset_id === p.asset_id));

    // Grouped rendering: a bypass leg renders indented directly under its
    // parent when both are on the job. Children whose parent isn't linked
    // stay in their normal position (still chip-labelled "Bypass of …").
    const byAssetId = new Map(assets.map((a) => [a.asset_id, a]));
    const childrenByParent = new Map<string, JobAssetRow[]>();
    for (const a of assets) {
        if (a.parent_asset_id && byAssetId.has(a.parent_asset_id)) {
            const arr = childrenByParent.get(a.parent_asset_id) ?? [];
            arr.push(a);
            childrenByParent.set(a.parent_asset_id, arr);
        }
    }
    const orderedAssets: Array<{ asset: JobAssetRow; isChild: boolean }> = [];
    for (const a of assets) {
        if (a.parent_asset_id && byAssetId.has(a.parent_asset_id)) continue; // rendered under parent
        orderedAssets.push({ asset: a, isChild: false });
        for (const child of childrenByParent.get(a.asset_id) ?? []) {
            orderedAssets.push({ asset: child, isChild: true });
        }
    }

    return (
        <Card ref={ref} id="results-card">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <ClipboardCheck className="h-4 w-4" /> Results
                        {assets.length > 0 && (
                            <Badge
                                variant={
                                    doneCount >= assets.length && assets.length > 0
                                        ? "default"
                                        : doneCount > 0
                                          ? "secondary"
                                          : "outline"
                                }
                                className="text-[10px]"
                            >
                                {preVisit
                                    ? `${assets.length} device${assets.length === 1 ? "" : "s"} expected`
                                    : doneCount === 0
                                      ? "Not started"
                                      : doneCount >= assets.length
                                        ? doneNoun.charAt(0).toUpperCase() + doneNoun.slice(1)
                                        : `${doneCount}/${assets.length} ${doneNoun}`}
                            </Badge>
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {/* #1300 — the office CAN drive the tech's own workflow
                            (submit-on-behalf has been supported since the gauge
                            attribution work), it was just never linked from
                            here, so it survived as folklore. When the app is
                            fine and the job merely needs finishing, this beats
                            the paper rip cord: it produces a full report with
                            readings. */}
                        {fieldWorkflowHref && (
                            <a
                                href={fieldWorkflowHref}
                                className="inline-flex h-8 items-center gap-1 rounded-md border border-input px-2.5 text-xs font-medium hover:bg-secondary"
                                title="Open the tech's own testing screens for this job and finish it from here"
                            >
                                <ExternalLink className="h-3.5 w-3.5" /> Open the tech workflow
                            </a>
                        )}
                        {onReconcile && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onReconcile}
                                title="Paste the city / authority device list — edits SITE records, then optionally syncs this job's device list"
                            >
                                <ListChecks className="h-3.5 w-3.5" /> Reconcile from list
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onAddDevice}
                            disabled={addDisabled}
                            title={addTitle}
                        >
                            <Plus className="h-3.5 w-3.5" /> Add device
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {siteFallbackCount > 0 && (
                    <div
                        className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                        data-testid="results-site-fallback-note"
                    >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>
                            <span className="font-medium">
                                {siteFallbackCount} device{siteFallbackCount === 1 ? "" : "s"} came from the location,
                                not the work order.
                            </span>{" "}
                            This work order didn&apos;t list any devices, so the location&apos;s active devices were
                            added so the tech isn&apos;t sent to an empty job. Confirm the list before dispatch and
                            remove anything this trip doesn&apos;t cover.
                        </p>
                    </div>
                )}
                {quotedDevicesNudge && assets.length === 0 && (
                    <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>
                            <span className="font-medium">Add the quoted devices.</span> This job was created from an
                            approved estimate, but no device serials could be parsed from the quote — link the devices
                            the estimate covers before dispatch.
                        </p>
                    </div>
                )}
                {assets.length === 0 && orphanTests.length === 0 ? (
                    tradeHasFamily ? (
                        /* Real empty state (spec §6d.4) — centered icon + one line +
                           a LARGE primary add button (≥44px tall), not a limp gray row. */
                        <div
                            className="flex flex-col items-center gap-3 py-8 text-center"
                            data-testid="results-empty-state"
                        >
                            <Gauge className="h-10 w-10 text-foreground/25" aria-hidden="true" />
                            <p className="text-sm text-foreground/70">No devices yet — add the first one</p>
                            <Button
                                size="lg"
                                className="min-h-11 px-6 text-base"
                                onClick={onAddDevice}
                                disabled={addDisabled}
                                title={addTitle}
                            >
                                <Plus className="h-5 w-5" /> Add device
                            </Button>
                        </div>
                    ) : (
                        <EmptyRow label="This job's trade isn't set, so no devices are shown" />
                    )
                ) : (
                    <div className="divide-y">
                        {orderedAssets.map(({ asset: a, isChild }) => (
                            <DeviceRow
                                key={a.link_id}
                                asset={a}
                                test={testByAsset.get(a.asset_id)}
                                inheritedTest={
                                    !testByAsset.has(a.asset_id) && a.parent_asset_id
                                        ? testByAsset.get(a.parent_asset_id)
                                        : undefined
                                }
                                hasRepair={repairedAssets.has(a.asset_id)}
                                repair={repairByAsset.get(a.asset_id)}
                                onRecordRepair={onRecordRepair}
                                recordRepairForName={recordRepairForName}
                                onRemoveRepair={onRemoveRepair}
                                onEdit={onEditAsset}
                                onUnlink={onUnlinkAsset}
                                unlinkPending={unlinkPending}
                                onPhotoClick={(photos, index) => setLightbox({ photos, index })}
                                isChild={isChild}
                                bypassChildSerial={
                                    childrenByParent.get(a.asset_id)?.length
                                        ? (childrenByParent.get(a.asset_id)![0].serial_number ?? "no serial")
                                        : undefined
                                }
                                protocols={protocolsByAsset.get(a.asset_id)}
                                onScope={onScopeAsset}
                                scopePendingAssetId={scopePendingAssetId}
                                onUsePaper={onUsePaper}
                                jobProof={jobProof}
                                onUploadReport={onUploadReport}
                            />
                        ))}
                        {orphanTests.map((t) => {
                            const photos = photosOf(t);
                            return (
                                <div key={t.id} className="py-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium">
                                            {[t.size_label, t.manufacturer, t.model].filter(Boolean).join(" ") ||
                                                t.asset_name}
                                            {t.serial_number ? ` S/N ${t.serial_number}` : ""}
                                        </span>
                                        <Badge variant={badgeVariant(t.overall_result)} className="text-[10px]">
                                            {testStateLabel(t.overall_result).toUpperCase()}
                                        </Badge>
                                        {/* #825 m5: "the most consequential three words on the
                                            page" — say what actually happened to the device. */}
                                        <span
                                            className="text-[11px] text-foreground/60"
                                            title="This device was removed from the job's device list — most often because its replacement was installed, which quietly detaches the old unit from the site record. The test result stays on file."
                                        >
                                            {new Date(t.performed_at).toLocaleDateString()} · no longer linked ⓘ
                                        </span>
                                    </div>
                                    <TesterLine test={t} />
                                    <PaperConversionLine test={t} />
                                    <OtherSiteLine test={t} />
                                    {photos.length > 0 && (
                                        <div className="mt-2 flex gap-1.5 overflow-x-auto">
                                            {photos.map((p, i) => (
                                                <button
                                                    key={`${p.slot}-${i}`}
                                                    type="button"
                                                    className="h-16 w-16 shrink-0 overflow-hidden rounded border bg-muted"
                                                    onClick={() => setLightbox({ photos, index: i })}
                                                    title={p.slot.replace(/_/g, " ")}
                                                >
                                                    <img
                                                        src={p.url}
                                                        alt={p.slot}
                                                        className="h-full w-full object-cover"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {orphanProtocols.map((p) => (
                            <div key={p.id} className="py-3">
                                <p className="mb-1 text-[11px] text-foreground/60">
                                    Device no longer linked to this job — the protocol still needs closing.
                                </p>
                                <FailProtocolFold protocol={p} />
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
            {lightbox && (
                <ImageLightbox
                    photos={lightbox.photos.map((p) => ({ src: p.url, alt: p.slot.replace(/_/g, " ") }))}
                    initialIndex={lightbox.index}
                    onClose={() => setLightbox(null)}
                />
            )}
        </Card>
    );
});
